'use client'
import { useRef, useState } from 'react'
import { useLanguage } from '@/lib/context/LanguageContext'

/**
 * Phone-only callback request — the primary closing CTA on the page.
 *
 * Submits through the existing /api/contact endpoint with sentinel values for
 * the fields the backend requires (name, company, email). The admin sees an
 * incoming lead clearly labelled as a callback request with a real phone.
 *
 * Inversion of the typical "you contact us" pattern: visitor gives a phone
 * number, MRN reaches out within 2 business days. Fits the brand's
 * regional-distributor positioning where MRN evaluates the relationship.
 */
export default function CallbackForm() {
  const { t, lang } = useLanguage()
  const [phone, setPhone] = useState('')
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)

    const trimmed = phone.trim()
    if (!trimmed) {
      setError(t.contact.callbackPhoneRequired)
      inputRef.current?.focus()
      return
    }
    // Strip everything but digits to count — accept 7+ digits to allow short
    // international formats without being too permissive.
    if (trimmed.replace(/\D/g, '').length < 7) {
      setError(t.contact.callbackPhoneInvalid)
      inputRef.current?.focus()
      return
    }

    setSubmitting(true)

    const fd = new FormData()
    fd.append('name', t.contact.callbackAdminLabel)
    fd.append('company', t.contact.callbackAdminLabel)
    fd.append('email', 'callback-request@noreply.mrn')
    fd.append('phone', trimmed)
    fd.append('message', t.contact.callbackAdminLabel)
    fd.append('locale', lang)
    fd.append('website', '')

    try {
      const res = await fetch('/api/contact', { method: 'POST', body: fd })
      if (!res.ok) {
        setError(t.contact.error)
        setSubmitting(false)
        return
      }
      setSubmitted(true)
    } catch {
      setError(t.contact.error)
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div role="status" className="mt-10 rounded-[var(--radius-card)] border border-surface/20 bg-surface/5 px-8 py-7 backdrop-blur">
        <p className="text-lg font-semibold text-surface">{t.contact.callbackSuccessTitle}</p>
        <p className="mt-2 text-sm text-surface/80">{t.contact.callbackSuccessSub}</p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="mt-10" noValidate>
      <label
        htmlFor="callback-phone"
        className="block text-xs uppercase tracking-[0.22em] text-surface/75 font-medium"
      >
        {t.contact.callbackPhoneLabel}
        <span aria-hidden className="text-accent-soft"> *</span>
        <span className="sr-only"> (required)</span>
      </label>
      <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-stretch">
        <input
          ref={inputRef}
          id="callback-phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          dir="ltr"
          required
          aria-required="true"
          aria-invalid={!!error}
          aria-describedby={error ? 'callback-phone-error' : undefined}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder={t.contact.callbackPhonePlaceholder}
          className="w-full sm:flex-1 rounded-none bg-surface/95 text-ink placeholder:text-ink-muted/60 px-5 py-3.5 text-base focus:outline-none focus:ring-2 focus:ring-surface/40 transition"
        />
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center justify-center gap-2 rounded-none bg-ink text-surface px-7 py-3.5 text-sm font-semibold disabled:opacity-60 hover:bg-ink/90 transition-colors whitespace-nowrap"
        >
          {submitting ? t.contact.callbackSubmitting : t.contact.callbackSubmit}
        </button>
      </div>
      {error && (
        <p id="callback-phone-error" role="alert" className="mt-3 text-sm text-accent-soft">
          {error}
        </p>
      )}
      <p className="mt-5 text-xs text-surface/70 leading-relaxed">
        {t.contact.callbackTiming}
      </p>
      <p className="mt-2 text-xs text-surface/65 leading-relaxed">
        {t.contact.callbackPrivacy}
      </p>
    </form>
  )
}
